import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, AlertCircle, CheckCircle } from 'lucide-react';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Textarea from '../components/ui/Textarea';
import Button from '../components/ui/Button';
import { useLanguage } from '../context/LanguageContext';
import { getFormattedHours } from '../data/openingHours';

export default function Contact() {
  const { t, language } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const hours = getFormattedHours(language);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-cream-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-primary-800 dark:bg-primary-900 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('contactPage.title')}
          </h1>
          <p className="text-lg text-primary-200 max-w-2xl mx-auto">
            {t('contactPage.subtitle')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card>
            {submitted ? (
              <div className="text-center py-8">
                <div className="inline-flex p-4 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-4">
                  <CheckCircle className="w-12 h-12 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-2xl font-display font-bold text-gray-900 dark:text-white mb-4">
                  {language === 'de' ? 'Nachricht gesendet!' : 'Message sent!'}
                </h3>
                <Button onClick={() => setSubmitted(false)} variant="outline">
                  {language === 'de' ? 'Neue Nachricht' : 'New message'}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="text"
                  name="name"
                  label={t('contactPage.form.name')}
                  placeholder={t('contactPage.form.namePlaceholder')}
                  value={formData.name}
                  onChange={handleChange}
                  required
                />

                <Input
                  type="email"
                  name="email"
                  label={t('contactPage.form.email')}
                  placeholder={t('contactPage.form.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                />

                <Input
                  type="text"
                  name="subject"
                  label={t('contactPage.form.subject')}
                  placeholder={t('contactPage.form.subjectPlaceholder')}
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />

                <Textarea
                  name="message"
                  label={t('contactPage.form.message')}
                  placeholder={t('contactPage.form.messagePlaceholder')}
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  required
                />

                {/* Demo Notice */}
                <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {t('contactPage.demoNotice')}
                  </p>
                </div>

                <Button type="submit" className="w-full" size="lg">
                  <Send className="w-5 h-5" />
                  {t('contactPage.form.submit')}
                </Button>
              </form>
            )}
          </Card>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* Address */}
            <Card className="flex items-start gap-4">
              <div className="p-3 bg-cream-100 dark:bg-gray-700 rounded-xl shrink-0">
                <MapPin className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {t('contactPage.info.address')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('contactPage.info.addressValue')}
                </p>
              </div>
            </Card>

            {/* Phone */}
            <Card className="flex items-start gap-4">
              <div className="p-3 bg-cream-100 dark:bg-gray-700 rounded-xl shrink-0">
                <Phone className="w-6 h-6 text-accent-600 dark:text-accent-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {t('contactPage.info.phone')}
                </h3>
                <a
                  href="tel:+4989987654"
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                >
                  {t('contactPage.info.phoneValue')}
                </a>
              </div>
            </Card>

            {/* Email */}
            <Card className="flex items-start gap-4">
              <div className="p-3 bg-cream-100 dark:bg-gray-700 rounded-xl shrink-0">
                <Mail className="w-6 h-6 text-wine-600 dark:text-wine-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {t('contactPage.info.email')}
                </h3>
                <a
                  href="mailto:info@bella-vista-muenchen.de"
                  className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                >
                  {t('contactPage.info.emailValue')}
                </a>
              </div>
            </Card>

            {/* Hours */}
            <Card>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-cream-100 dark:bg-gray-700 rounded-xl">
                  <Clock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {t('contactPage.info.hours')}
                </h3>
              </div>
              <div className="space-y-2">
                {hours.map((item, index) => (
                  <div
                    key={index}
                    className={`flex justify-between text-sm ${
                      item.closed ? 'text-wine-600 dark:text-wine-400' : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <span>{item.day}</span>
                    <span>{item.hours}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Map Placeholder */}
            <div className="rounded-xl overflow-hidden h-48 bg-cream-200 dark:bg-gray-700 relative">
              <img
                src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600&h=300&fit=crop"
                alt="Munich map"
                className="w-full h-full object-cover opacity-50"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <Button
                  href="https://maps.google.com/?q=Maximilianstraße+45+München"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="secondary"
                  size="sm"
                >
                  {t('location.getDirections')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
